/* NetworkDiscoveryDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./NetworkDiscoveryDetailsViewRenderer");
const ScheduleVeilComponent = require("./veils/createeditschedule/ScheduleVeilComponent");
const escapeHtml = require("@gluenetworks/dom_utilities").escapeHtml;

class NetworkDiscoveryComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        LicenseRestriction,
        MUF,
        storageFactory,
        NetworkDiscoveryPlan,
        WorkEntity,
        steveIo,
        CredentialsEntity,
        DeviceEntity,
        ConnectionInfo,
        jnibAdapter,
        NetworkDiscoveryExclusions,
        OrgZone
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._licenseRestriction = LicenseRestriction;
        this._modalUserFeedback = MUF;
        this._storageFactory = storageFactory;
        this._networkDiscoveryPlanEntityDef = NetworkDiscoveryPlan;
        this._workEntity = WorkEntity;
        this._steveIo = steveIo;
        this._credentialsEntityDef = CredentialsEntity;
        this._deviceEntityDef = DeviceEntity;
        this._connectionInfoEntityDef = ConnectionInfo;
        this._nibApiService = jnibAdapter.getNibApiService();
        this._NetworkDiscoveryExclusionsEntity = NetworkDiscoveryExclusions;
        this._orgZoneEntityDef = OrgZone;

        this._networkDiscoveryPlanEntity = undefined;
        this._credentialsSlick = [];
        this._exclusionsSlickData = [];
        this._networkDiscoveryExclusionEntity = [];
        this._typeBaseMap = {};
        this._exclusionTypes = {
            deviceType: "Device Type",
            hostName: "Hostname",
            ipAddress: "IP Address",
            ipBlock: "IP Block",
            ipRange: "IP Range",
            serialNumber: "Serial Number",
        };
        this._isPlanValid = false;

        this._newPlanNoEngineZone = false;

        // Flag to see if OSM SOlution is available for the license
        this._isOSMSolutionAvailable = this._licenseRestriction.checkLicense("OSM");
        this._onLastWorkIdUpdated = this._onLastWorkIdUpdated.bind(this);

        this._renderer = new Renderer(this);
    }

    _registerListeners() {
        this._networkDiscoveryPlanEntity.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );

        this._networkDiscoveryPlanEntity.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );

        this._networkDiscoveryPlanEntity.on("LastWorkIdPropertyUpdated", this._onLastWorkIdUpdated);
    }

    _unregisterListeners() {
        this._networkDiscoveryPlanEntity.removeListener(
            "PristinePropertiesChanged",
            this._onPristinePropertiesChanged
        );
        this._networkDiscoveryPlanEntity.removeListener(
            "DirtyStateChanged",
            this._onDirtyStateChanged
        );
        this._networkDiscoveryPlanEntity.removeAllListeners("LastWorkIdPropertyUpdated");
    }

    /*******************************************/
    /* START HELPER METHODS                    */
    /*******************************************/

    /**
     *
     * @param {} buttonValue - Name of button
     * @param {} buttonText - Text that would show up on this button
     */
    _setViewActionText(buttonValue, buttonText) {
        this._gameEditor._setViewActionText(buttonValue, buttonText);
    }

    _findKeyInExclusion(exclusionObj) {
        const keysToFind = Object.keys(this._exclusionTypes);
        const exclusionKeys = Object.keys(exclusionObj);
        const foundKey = keysToFind.find((key) => exclusionKeys.includes(key));

        return foundKey;
    }

    /*******************************************/
    /* END HELPER METHODS                      */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._renderer,
                    this._logger,
                    this._gameEditor,
                    this._userSession,
                    this._licenseRestriction,
                    this._modalUserFeedback,
                    this._storageFactory,
                    this._workEntity,
                    this._steveIo,
                    this._credentialsEntityDef,
                    this._engineZoneDropDownValues,
                    this._isPlanValid,
                    this._orgZoneEntityDef,
                    (this._isOSMSolutionAvailable = undefined);
            },
            (err) => {
                this._logger.warn(err, "NetworkDiscoveryComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _setDirty(state) {
        this._gameEditor._setDirty(state);
    }

    _onDirtyStateChanged(dirtyState) {
        if (this._gameEditor) {
            this.emit("Dirty", dirtyState);

            let onBeforeUnloadFunc = () => {
                return this._modalUserFeedback
                    .displayConfirmation("Would you like to Save or Discard your changes?", {
                        title: "Unsaved Changes",
                        confirmDetails: {
                            text: "Save",
                            type: "additive",
                        },
                        rejectDetails: {
                            text: "Discard",
                            type: "text",
                        },
                    })
                    .then(() => {
                        if (this._isPlanValid) {
                            this.saveEntity();
                        }
                    })
                    .catch((error) => {
                        if (error.userRejected) {
                            return Promise.resolve()
                                .then(() => this._modalUserFeedback.hideDisplayed())
                                .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                                .then(() => undefined);
                        } else {
                            this._logger.warn({error}, "Error in onBeforeUnloadFunc! Err:");
                        }
                    });
            };

            //this will warn the user of updates, but not of add operations.  There will never be delete.
            if (this._networkDiscoveryPlanEntity.isDirty()) {
                this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
            } else {
                this._gameEditor.clearOnBeforeUnload();
            }
        }
    }

    _onPristinePropertiesChanged(pristineValues) {
        this.emit("Status", pristineValues.status);

        this.emit("NetworkDiscoveryPropertiesChanged");
    }

    _onLastWorkIdUpdated() {
        this._gameEditor._currentDiscoveryWorkId = this._networkDiscoveryPlanEntity.lastWorkId;
        this.emit("DiscoveryWorkIdUpdated");
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    get activeOrgId() {
        return this._userSession.activeOrganization.id;
    }

    get isDirty() {
        return this._networkDiscoveryPlanEntity.isDirty();
    }

    async whenModelIsReady() {
        try {
            this._networkDiscoveryPlanEntity = await this._gameEditor.whenGameEditorReady();
            this._gameEditor._currentDiscoveryWorkId = this._networkDiscoveryPlanEntity.lastWorkId;

            await this._whenModelReadyPromises();

            await this._getTypeBaseMap();
            this.setValuesForGrids();

            this._registerListeners();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    _whenModelReadyPromises() {
        return Promise.all([
            this._loadEngineZoneValues(),
            this._loadConnectionInfoJtype(),
            this._loadCredentials(),
            this._getNetworkDiscoveryExclusionEntities(),
        ]);
    }

    // Methods to fetch Data - Start

    /**
     * Load Engine Zone Dropdown values
     */
    async _loadEngineZoneValues() {
        try {
            const _engineZoneValues = await this._orgZoneEntityDef.loadAscendOrgZones(
                this.activeOrgId,
                {},
                {}
            );
            this._engineZoneDropDownValues = _engineZoneValues
                .filter((engineZone) => engineZone.getZoneEnabled() === true)
                .map((engineZone) => ({
                    label: engineZone.getName(),
                    value: engineZone.getEngineZoneId(),
                    default: engineZone.getDefaultZone() || false,
                }));

            if (_.isEmpty(this._networkDiscoveryPlanEntity.getEngineZoneId())) {
                this._newPlanNoEngineZone = true;
                const defaultEngineZone = this._engineZoneDropDownValues.find(
                    (i) => i.default === true
                );

                const engineZoneVal = !_.isEmpty(defaultEngineZone)
                    ? defaultEngineZone.value
                    : _.isArray(this._engineZoneDropDownValues) &&
                      this._engineZoneDropDownValues.length > 0 &&
                      this._engineZoneDropDownValues[0].value
                    ? this._engineZoneDropDownValues[0].value
                    : "";

                if (engineZoneVal) {
                    this._networkDiscoveryPlanEntity.setEngineZoneId(engineZoneVal);

                    const zoneObj =
                        this._engineZoneDropDownValues.find((i) => i.value === engineZoneVal) || {};

                    this._networkDiscoveryPlanEntity.setZoneName(zoneObj.label);

                    await this._networkDiscoveryPlanEntity.update({skipAudit: true});
                }
            }

            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    /**
     * Load connection info jtype
     */
    async _loadConnectionInfoJtype() {
        this._connectionInfoJtype = await this._connectionInfoEntityDef.buildJtypeSelectOptions(
            this.activeOrgId
        );
    }

    async _loadCredentials() {
        try {
            this._credentials = await this._credentialsEntityDef.loadCredentials(this.activeOrgId);

            const deviceUserPass = this._credentials.filter(
                (cred) => cred.getCredsType() === "deviceUserPass"
            );
            const enableMode = this._credentials.filter(
                (cred) => cred.getCredsType() === "deviceEnablePass"
            );

            this._deviceUserPass = deviceUserPass.map((cred) => ({
                label: cred.getName(),
                value: `{{credentials.${cred.getId()}}}`,
            }));

            this._enableMode = enableMode.map((cred) => ({
                label: cred.getName(),
                value: `{{credentials.${cred.getId()}}}`,
            }));

            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async _getNetworkDiscoveryExclusionEntities() {
        try {
            this._networkDiscoveryExclusionEntity =
                await this._NetworkDiscoveryExclusionsEntity.loadNetworkDiscoveryExclusions(
                    this.activeOrgId
                );
            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    _getTypeBaseMap() {
        return this._deviceEntityDef
            .loadAndMergeTypeBaseMap(this.activeOrgId)
            .then((typebaseMap) => {
                this._typeBaseMap = typebaseMap;
                return Promise.resolve();
            });
    }

    _loadScheduleFeed() {
        this._scheduleFeed = this._networkDiscoveryPlanEntity.startRelatedScheduleFeed({});
        return this._scheduleFeed.whenInitialized();
    }

    // Methods to fetch Data - End

    // Helper

    _resetView() {
        this._networkDiscoveryPlanEntity.clearPendingPatches(true);

        this.setValuesForGrids();

        this.emit("ResetView");
    }

    // Helper

    // Grids Data - Start

    setValuesForGrids() {
        // Ranges Grid - Start
        const rangesValues = this._networkDiscoveryPlanEntity.getTargets() || [];
        this._targets = [];
        rangesValues.forEach((range, idx) => {
            if (range.ipRange) {
                this._targets.push({
                    id: idx,
                    type: "ipRange",
                    ipRangeValues: `${range.ipRange.start} - ${range.ipRange.end}`,
                });
            } else if (range.ipBlock) {
                this._targets.push({id: idx, type: "ipBlock", ipRangeValues: range.ipBlock});
            }
        });
        // Ranges Grid - End

        // Credentials Grid - Start
        const loginValues = this._networkDiscoveryPlanEntity.getCredentials() || [];
        const enableModeValues = this._networkDiscoveryPlanEntity.getEnablePasswords() || [];
        this._credentialsSlick = [];
        let idxCred = 0;
        let credentialsUpdated = this._networkDiscoveryPlanEntity.getCredentials();
        loginValues.map((login) => {
            let loginVal = typeof login === "string" ? login : "";
            if (loginVal.indexOf("{{credentials.") > -1) {
                loginVal = loginVal.replace("{{credentials.", "").replace("}}", "");
            }

            const credentialEntity = this._credentials.find((cred) => `${cred.id}` === loginVal);
            if (_.isEmpty(credentialEntity)) {
                // Case if entity is not present in Org. It's added to the credentialsUpdated Array
                credentialsUpdated = credentialsUpdated.filter(
                    (i) => i !== (typeof login === "string" ? login : "")
                );
            } else {
                this._credentialsSlick.push({
                    id: idxCred++,
                    type: "login",
                    credId: loginVal,
                    credValue: credentialEntity.getName(),
                });
            }
        });

        // If there's deviation between credentialsUpdated and loginValues, credentialsUpdated is put in the Entity
        if (!_.isEqual(credentialsUpdated, loginValues)) {
            this._networkDiscoveryPlanEntity.setCredentials(credentialsUpdated);
        }

        let enableModeUpdated = this._networkDiscoveryPlanEntity.getEnablePasswords() || [];
        enableModeValues.map((enableMode) => {
            let enableModeVal = typeof enableMode === "string" ? enableMode : "";
            if (enableModeVal.indexOf("{{credentials.") > -1) {
                enableModeVal = enableModeVal.replace("{{credentials.", "").replace("}}", "");
            }
            const credentialEntity = this._credentials.find(
                (cred) => `${cred.id}` === enableModeVal
            );

            if (_.isEmpty(credentialEntity)) {
                // Case if entity is not present in Org. It's added to the enableModeUpdated Array
                enableModeUpdated = enableModeUpdated.filter(
                    (i) => i !== (typeof enableMode === "string" ? enableMode : "")
                );
            } else {
                this._credentialsSlick.push({
                    id: idxCred++,
                    type: "enableMode",
                    credId: enableModeVal,
                    credValue: credentialEntity.getName(),
                });
            }
        });

        // If there's deviation between enableModeUpdated and enableModeValues, enableModeUpdated is put in the Entity
        if (!_.isEqual(enableModeUpdated, enableModeValues)) {
            this._networkDiscoveryPlanEntity.setEnablePasswords(enableModeUpdated);
        }

        // Here, if the plan is dirty, it's saved silently.
        // This method is only called on load of page or cancel button click,
        // either cases, there shouldn't be any other dirty value other than the above
        if (this._networkDiscoveryPlanEntity.isDirty()) {
            this._networkDiscoveryPlanEntity.update({skipAudit: true});
        }

        // Credentials Grid - End

        // Exclusions Grid - Start
        let idx = 0;
        this._exclusionsSlickData = (
            this._networkDiscoveryExclusionEntity.getExclusions() || []
        ).map((exclusion) => {
            const val = this._findKeyInExclusion(exclusion);
            return {
                id: idx++,
                type: val,
                description: exclusion["description"] || "",
                exclusionValue: escapeHtml(exclusion[val]),
                typeBaseMap: this._typeBaseMap,
                orgLevel: true,
            };
        });

        this._exclusionsSlickData = [
            ...this._exclusionsSlickData,
            ...(this._networkDiscoveryPlanEntity.getExclusions() || []).map((exclusion) => ({
                id: idx++,
                type: this._findKeyInExclusion(exclusion),
                description: exclusion["description"] || "",
                exclusionValue: escapeHtml(exclusion[this._findKeyInExclusion(exclusion)]),
                typeBaseMap: this._typeBaseMap,
                orgLevel: false,
            })),
        ];

        // Exclusions Grid - End
    }

    // Grids Data - End

    /**
     * Entity Update Methods - Start
     */

    addTargets(result) {
        let resObj = {};
        let valueForSlick = "";
        if (result.type === "ipRange") {
            resObj = {ipRange: {start: result.start, end: result.end}};
            valueForSlick = `${result.start} - ${result.end}`;
        } else {
            resObj = {ipBlock: result.ipBlock};
            valueForSlick = result.ipBlock;
        }

        const targets = this._networkDiscoveryPlanEntity.getTargets() || [];

        targets.push(resObj);

        this._networkDiscoveryPlanEntity.setTargets(targets);

        const slickTarget = {
            id: Math.floor(100000 + Math.random() * 900000),
            type: result.type,
            ipRangeValues: valueForSlick,
        };

        this._targets.push(slickTarget);

        this.emit("UpdateTargetsGrid");
    }

    removeTargets(id) {
        // Update this._target object for slick grid
        const newTargets = this._targets.filter((item) => `${item.id}` !== id);
        this._targets = newTargets;

        this._convertTargetsSlickToEntity();

        this.emit("UpdateTargetsGrid");
    }

    updateTarget(id, newValues) {
        const updatedTargetIndex = this._targets.findIndex((i) => `${i.id}` === id);

        if (newValues.type === "ipRange") {
            this._targets[updatedTargetIndex].type = "ipRange";
            this._targets[
                updatedTargetIndex
            ].ipRangeValues = `${newValues.start} - ${newValues.end}`;
        } else if (newValues.type === "ipBlock") {
            this._targets[updatedTargetIndex].type = "ipBlock";
            this._targets[updatedTargetIndex].ipRangeValues = newValues.ipBlock;
        }

        this._convertTargetsSlickToEntity();

        this.emit("UpdateTargetsGrid");
    }

    _convertTargetsSlickToEntity() {
        const dataForEntity = [];
        this._targets.forEach((item) => {
            if (item.type === "ipRange") {
                const startAndEnd = item.ipRangeValues.split(" - ");
                dataForEntity.push({
                    ipRange: {start: _.trim(startAndEnd[0]), end: _.trim(startAndEnd[1])},
                });
            } else {
                dataForEntity.push({ipBlock: item.ipRangeValues});
            }
        });

        this._networkDiscoveryPlanEntity.setTargets(dataForEntity);
    }

    // Credentials - Start

    addCredentials(res) {
        const credentialEntity = this._credentials.find((cred) => cred.getId() === res.credValue),
            credentialEntityName = credentialEntity ? credentialEntity.getName() : "";
        this._credentialsSlick.push({
            id: Math.floor(100000 + Math.random() * 900000),
            type: res.type,
            credValue: credentialEntityName,
            credId: res.credValue,
        });

        this._convertCredentialsSlickToEntity();

        this.emit("UpdateCredentialsGrid");
    }

    removeCredential(id) {
        const newCredentials = this._credentialsSlick.filter((item) => `${item.id}` !== id);
        this._credentialsSlick = newCredentials;

        this._convertCredentialsSlickToEntity();

        this.emit("UpdateCredentialsGrid");
    }

    updateCredential(id, res) {
        const credentialIndex = this._credentialsSlick.findIndex((i) => `${i.id}` === id),
            entityId = res.credValue.replace("{{credentials.", "").replace("}}", ""),
            credentialEntity = this._credentials.find((cred) => cred.getId() === entityId),
            credentialEntityName = credentialEntity ? credentialEntity.getName() : "";

        this._credentialsSlick[credentialIndex] = {
            id: this._credentialsSlick[credentialIndex].id,
            type: res.type,
            credValue: credentialEntityName,
            credId: entityId,
        };

        this._convertCredentialsSlickToEntity();

        this.emit("UpdateCredentialsGrid");
    }

    _convertCredentialsSlickToEntity() {
        const dataForCredentials = [],
            dataForEnableMode = [];
        this._credentialsSlick.forEach((item) => {
            if (item.type === "login") {
                dataForCredentials.push(`{{credentials.${item.credId}}}`);
            } else {
                dataForEnableMode.push(`{{credentials.${item.credId}}}`);
            }
        });

        this._networkDiscoveryPlanEntity.setCredentials(dataForCredentials);
        this._networkDiscoveryPlanEntity.setEnablePasswords(dataForEnableMode);
    }

    // Credentials - End

    // Exclusions - Start

    _addExclusion(res) {
        const val = this._findKeyInExclusion(res);
        this._exclusionsSlickData.push({
            id: Math.floor(100000 + Math.random() * 900000),
            type: val,
            description: res["description"] || "",
            exclusionValue: res[val],
            typeBaseMap: this._typeBaseMap,
            orgLevel: false,
        });

        const exclusions = this._networkDiscoveryPlanEntity.getExclusions() || [];
        exclusions.push(res);
        this._networkDiscoveryPlanEntity.setExclusions(exclusions);

        this.emit("UpdateExclusionsGrid");
    }

    _updateExclusion(id, res) {
        const exclusionIndex = this._exclusionsSlickData.findIndex((i) => i.id === id);

        _.set(this._exclusionsSlickData[exclusionIndex], "type", _.keys(res)[0]);
        _.set(this._exclusionsSlickData[exclusionIndex], "description", res["description"] || "");
        _.set(this._exclusionsSlickData[exclusionIndex], "exclusionValue", res[_.keys(res)[0]]);

        this._convertExclusionSlickToEntity();

        this.emit("UpdateExclusionsGrid");
    }

    removeExclusion(id) {
        this._exclusionsSlickData = this._exclusionsSlickData.filter((i) => `${i.id}` !== id);

        this._convertExclusionSlickToEntity();

        this.emit("UpdateExclusionsGrid");
    }

    _convertExclusionSlickToEntity() {
        const nonOrgExclusions =
            this._exclusionsSlickData.filter((exclusion) => exclusion.orgLevel !== true) || [];

        this._networkDiscoveryPlanEntity.setExclusions(
            nonOrgExclusions.map((exclusion) => ({
                [exclusion.type]: exclusion.exclusionValue,
                description: exclusion.description,
            }))
        );
    }

    // Exclusions - End

    _setTopEntityFields(obj) {
        this._networkDiscoveryPlanEntity.setName(obj.name || "");
        this._networkDiscoveryPlanEntity.setDescription(obj.description || "");
        this._networkDiscoveryPlanEntity.setEngineZoneId(obj.engineZone);
        this._networkDiscoveryPlanEntity.setZoneName(obj.engineZoneName);
    }

    _setRangesConnectionFields(obj) {
        this._networkDiscoveryPlanEntity.setConnectionType(obj.connectionType);
        this._networkDiscoveryPlanEntity.setScanPort(obj.port);
    }

    _setModeValue(val) {
        this._networkDiscoveryPlanEntity.setMode(val);
    }

    setConnectionInformation(connVal) {
        this._networkDiscoveryPlanEntity.setConnectionInformation(connVal);
    }

    setOptionsSectionEntityFields(optionsValues) {
        this._networkDiscoveryPlanEntity.setSearchType(optionsValues.searchType);
        this._networkDiscoveryPlanEntity.setSearchDepth(optionsValues.searchDepth);
        this._networkDiscoveryPlanEntity.setUseAsProxy(optionsValues.useAsProxy);
        this._networkDiscoveryPlanEntity.setUseArp(optionsValues.useArp);
        this._networkDiscoveryPlanEntity.setVrf(optionsValues.vrf || null);
        this._networkDiscoveryPlanEntity.setUseCdp(optionsValues.useCdp);
        this._networkDiscoveryPlanEntity.setUseLldp(optionsValues.useLldp);

        if (!_.isEmpty(optionsValues.vrf)) {
            this._networkDiscoveryPlanEntity.setUseVrf(true);
        } else {
            this._networkDiscoveryPlanEntity.setUseVrf(false);
        }
    }

    _setSchedule(schedule) {
        return this._networkDiscoveryPlanEntityDef
            .loadNetworkDiscoveryPlan(this.activeOrgId, this._networkDiscoveryPlanEntity.getId(), {
                schedule: true,
                scheduleEnabled: true,
                scheduleWorkInput: true,
            })
            .then((networkDiscoveryPlanEntity) => {
                !_.isUndefined(schedule.schedule) &&
                    networkDiscoveryPlanEntity.setSchedule(schedule.schedule);
                networkDiscoveryPlanEntity.setScheduleEnabled(schedule.scheduleEnabled);
                !(_.isUndefined(schedule.autoAddDevices) && _.isUndefined(schedule.workInput)) &&
                    networkDiscoveryPlanEntity.setScheduleWorkInput(
                        Object.assign(
                            {},
                            {autoAddDevices: schedule.autoAddDevices},
                            schedule.workInput
                        )
                    );
                return networkDiscoveryPlanEntity.update();
            });
    }

    _setStatus(status) {
        this._networkDiscoveryPlanEntity.setStatus(status);
    }

    saveEntity() {
        if (this._networkDiscoveryPlanEntity.isDirty()) {
            this._networkDiscoveryPlanEntity.setStatus("READY");
            return this._networkDiscoveryPlanEntity.update();
        }

        return Promise.resolve();
    }

    displaySaveError(err) {
        this._modalUserFeedback.displayError(err);
    }

    /**
     * Entity Update Methods - End
     */

    /**
     * Work Methods - Start
     */

    async runNetworkDiscoveryWork() {
        try {
            this._gameEditor._myRunPlan = true;
            this._gameEditor._currentDiscoveryWorkId = null;

            // const currentExecutionId = this._networkDiscoveryPlanEntity.getLastWorkId();

            const progressCB = async (data) => {
                // console.log("progress", data);
                if (data.workId) {
                    this._gameEditor._currentDiscoveryWorkId = data.workId;
                }
            };

            const workInput = {
                tenantId: this.activeOrgId,
                id: this._networkDiscoveryPlanEntity.id,
                description: `Network Discovery work for ${this.activeOrgId}`,
            };

            await this._networkDiscoveryPlanEntity.runNetworkDiscoveryWork(
                workInput,
                {launchAndForget: false},
                progressCB
            );
            this._gameEditor._currentDiscoveryWorkId = null;

            this.emit("DiscoveryCompleted");
        } catch (error) {
            this._logger.warn({error}, "NetworkDiscovery::Plan run error");
        }
    }

    async cancelNetworkDiscoveryWork() {
        if (this._gameEditor._currentDiscoveryWorkId) {
            try {
                if (this._networkDiscoveryPlanEntity.getStatus() === "COMPLETE") {
                    const err = {
                        errorCode: "",
                        errorMsg: "Network Discovery job completed",
                        errorTitle: "Cancel Network Discovery",
                    };
                    await this._modalUserFeedback.displayError(err);
                } else {
                    await this._modalUserFeedback.displayConfirmation(
                        "All running Network Discovery jobs will be canceled immediately. Are you sure you want to continue?",
                        {
                            title: "Cancel Network Discovery",
                            confirmDetails: {
                                text: "Confirm",
                                type: "destructive",
                            },
                            rejectDetails: {
                                text: "Cancel",
                                type: "text",
                            },
                        }
                    );
                    this._planRunning = false;
                    this._networkDiscoveryPlanEntityDef.cancelWork(
                        this.activeOrgId,
                        this._gameEditor._currentDiscoveryWorkId
                    );
                    this._gameEditor._currentDiscoveryWorkId = undefined;
                }
            } catch (err) {
                this._logger.warn(err, "Network discovery error");
            }
        } else {
            try {
                const err = {
                    errorCode: "",
                    errorMsg: "Network Discovery job not yet started",
                    errorTitle: "Cancel Network Discovery",
                };
                await this._modalUserFeedback.displayError(err);
            } catch (err) {
                this._logger.warn(err, "Network discovery error");
            }
        }
    }

    _scheduleDiscovery() {
        return this._modalUserFeedback
            .displayCustom(
                new ScheduleVeilComponent(this._logger, true, "Schedule Network Discovery Plan", {
                    enabled: this._networkDiscoveryPlanEntity.getScheduleEnabled(),
                    scheduleObj: this._networkDiscoveryPlanEntity.getScheduleWorkInput(),
                    schedule: this._networkDiscoveryPlanEntity.getSchedule(),
                }),
                {
                    dontHide: true,
                }
            )
            .then((schedule) => {
                const updatedEnableSchedule = schedule.scheduleEnabled;
                const planScheduleEnabled = this._networkDiscoveryPlanEntity.getScheduleEnabled();
                if (
                    updatedEnableSchedule !== planScheduleEnabled &&
                    updatedEnableSchedule === false
                ) {
                    return this._modalUserFeedback
                        .displayConfirmation(
                            "Disabling the schedule will delete the schedule and all of its history. \
                        A schedule can be paused in the Schedules solution. Are you sure you wish to delete?",
                            {
                                title: "Disable Schedule",
                                confirmDetails: {
                                    text: "Delete",
                                    type: "destructive",
                                },
                                rejectDetails: {
                                    text: "Cancel",
                                    type: "text",
                                },
                            }
                        )
                        .then(() => {
                            return Promise.resolve(schedule);
                        });
                }
                return Promise.resolve(schedule);
            })
            .then((schedule) => {
                return this._setSchedule(schedule);
            })
            .catch((err) => {
                if (!err.userRejected) {
                    this._logger.warn(err);
                }
            })
            .finally(() => {
                this._modalUserFeedback.hideDisplayed();
            });
    }

    /**
     * Work Methods - End
     */

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = NetworkDiscoveryComponent;

NetworkDiscoveryComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.licenseRestriction",
    "$teve.ui.userFeedback",
    "$teve.storageFactory",
    "$teve.entities.NetworkDiscoveryPlan",
    "$teve.entities.Work",
    "$teve.io",
    "$teve.entities.Credentials",
    "$teve.entities.Device",
    "$teve.entities.ConnectionInfo",
    "$teve.jnibAdapter",
    "$teve.entities.NetworkDiscoveryExclusions",
    "$teve.entities.OrgZone",
];

NetworkDiscoveryComponent.$displayDetails = {
    type: "View",
    name: "Network Discovery Details",
    icon: "devicemanager.network_discovery_view",
    shown: true,
    title: "Details",
    order: 1,
};
